// Domain model for the APG CoPilot (sibling of the Hedging Grid CoPilot).
//
// Frontend-owned: NOTHING here persists server-side. Pure TypeScript (no Angular) so the
// invariants can be unit-tested directly. This is an ISOLATED SIBLING of the Hedge CoPilot:
// it re-implements the proven session/Entity-Value DESIGN, it does NOT import from
// hedging-grid/copilot (zero coupling to the shipped feature).
//
// ES5 note: arrays only; no for..of / spread over Set/Map (project rule).

import { CashFlowAdjustment } from '../model/CashFlowAdjustment';

// ---- Tabs -----------------------------------------------------------------------------
// The APG CoPilot WIDENS the vending-machine idea: ONE runnable tab (rollPositions) + ONE
// read-only tab (zone — no Run button, no inputs, no cards).
export type TabKind = 'rollPositions' | 'zone';

// ---- Roll Positions questionnaire inputs ----------------------------------------------

export type Weekday = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri';

export const WEEKDAYS: Weekday[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Date selection — a SINGLE date XOR a date RANGE. A single date is a complete choice (no
// exclusions apply); a range opens the two kinds of exclusion below.
export type RollDateSelection =
    | { kind: 'single'; single: Date | null }
    | { kind: 'range'; start: Date | null; end: Date | null };

export type BiasDirection = 'up' | 'down' | 'sideways' | 'auto';

export interface RollBias {
    enabled: boolean;
    direction: BiasDirection | null;   // non-null only while enabled
}

export interface RollPositionsInputs {
    date: RollDateSelection;
    // Only meaningful with a range. The STARTING set is the host's "Weekdays to exclude" setting
    // (CR-20260915-002): Monday for the APG ORION (BR-D1), NONE for Maven; the trader toggles
    // from there.
    weekdayExclusions: Weekday[];
    specificDateExclusions: Date[];     // one-off carve-outs INSIDE the range
    // CR-20260904-001 (BR-W3/BR-W4), generalised by CR-20260915-002: the days the trader brought
    // BACK from the weekday rule, one at a time - any excluded weekday, no longer Mondays only.
    // The weekday default is a default, not a wall; this is the escape hatch. An entry whose
    // weekday is not currently excluded is ignored by every reader. REQUIRED (never optional): an
    // optional field would mint a third "not set" state the rules do not have and force every
    // reader to null-guard. Nothing deserializes this type, so no stored object can arrive
    // without it.
    weekdayOverrides: Date[];
    bias: RollBias;
}

// Small payload the questionnaire emits up to the orchestrator (drives RUN enable).
export interface RollTabState {
    inputs: RollPositionsInputs;
    valid: boolean;
}

// "Even five's excessive" — the DEFAULT ranked short-list cap. The live cap is the shell-level
// "Show top N results" ORION setting; this constant is only the fallback when it is not set.
//
// ⚠ WHERE THE CAP IS APPLIED CHANGED ON 2026-09-10 (CR-20260910-001, ruling R-B). It used to travel
// on the run request and cut the list before the cards were handed over. It is now the results
// list's DISPLAY limit, applied at render after the trader's own sort, and it never reaches the
// wire: the run delivers every window date because the Price Box feeds its columns from that same
// delivery, so an earlier cut would blank the columns of every date it dropped.
export const MAX_ROLL_CARDS = 5;

// ---- CR-20260904-001, Filtering rules: what ONE suggestion is ------------------------------
// 'cell'  = today's card identity: one card per Price Box CELL (adjustment type x date), and the
//           50% walk (BR-S2) runs per adjustment-type ROW. This is the RULED INTERIM (leader,
//           2026-09-04; owner question O19), because the owner's own walk compared dates INSIDE
//           one row and the Price Box rows are his model of "an adjustment".
// 'day'   = one card per DAY, the day's credit being its best surviving cell.
// The owner's answer on O19 is therefore ONE LINE either way. Nothing else in the milestone
// depends on the choice.
export type SuggestionGranularity = 'cell' | 'day';
export const SUGGESTION_GRANULARITY: SuggestionGranularity = 'cell';

// ---- Preview key (advisory highlight of ONE Price Box cell) ---------------------------
// A cell = adjustment TYPE(s) (row) x expiration (column). This carries NO positions/legs;
// it only locates the matching Price Box cell for the advisory highlight.
export interface RollPreviewKey {
    adjustmentType: string;
    adjustmentType2?: string | null;
    expirationDatePretty: string;       // e.g. "26-May-23" — the PriceboxColumn key
}

export type CostDirection = 'credit' | 'debit';

// ---- Reason line parts (CR-20260904-001, REQ-8) ---------------------------------------
// The skill writes everything about the reason EXCEPT the money, and the card supplies the money
// from the live price accessor the header already reads. One source, so header and reason cannot
// diverge while quotes tick.
//
// Composed as:  lead + ("collects a $" | "pays a $") + amount + (" credit" | " debit") + tail
// e.g.          "Rolls to 7750 and " + "collects a $" + "32.38" + " credit" + ", clear of economic reports."
export interface RollSuggestionReason {
    /** Everything before the money. Ends with a space. */
    lead: string;
    /** Everything after the money, including the full stop. */
    tail: string;
}

// ---- Solution card content (skill-authored Value; Entity/Value split) -----------------
// Rendered verbatim; the host never re-judges it. NEVER carries the trade legs onto the card
// (legs live only in the Review dialog).
export interface RollSuggestionContent {
    // OPTIONAL since the owner's ruling O42 (2026-09-04): the badge is off the card and the run
    // computes no score at all. NOTHING WRITES IT AND NOTHING RENDERS IT since 2026-09-10 — the mock
    // skill that used to sort by it was deleted with the browser skill (CR-20260910-001 milestone 1,
    // leader ruling Q4). The field is kept only so an older stored card shape still satisfies the
    // type; do not start populating it without an owner ruling, because the card is deliberately
    // clean and the sort control is what says how the list is ordered.
    score?: number;
    // CR follow-on 2026-09-07: THE ORDERING FIGURE — what this roll pays divided by its days to
    // expiration, signed (a debit is negative). DATA, NOT DISPLAY: owner ruling O56 keeps the card
    // clean, and the sort control is what says how the list is ordered.
    //
    // It is carried on the content so the results toolbar re-sorts on the EXACT number the run
    // ranked on, rather than re-deriving one from `cost` (absolute, rounded for display) and
    // `costDirection`. A second derivation is a second definition of "better", and it would drift.
    // Absent on a card from a skill that does not rank (the mock), which sorts last in either
    // direction rather than being interleaved on a guess.
    perDayCredit?: number;
    adjustmentDate: string;             // pretty expiration date, e.g. "26-May-23"
    daysOut: number;
    adjustmentTypes: string[];          // e.g. ["2B(C)", "2B(P)"]
    currentStrike: string;
    destinationStrike: string;
    direction: string;
    costDirection: CostDirection;
    cost: number;                       // absolute money SNAPSHOT at run time; sign is carried by costDirection
    // Live signed cell price (quote-driven PriceboxColumn.price). The card reads this every CD tick so the
    // displayed cost ticks in real-time like the Price Box; absent/NaN => fall back to the frozen `cost`.
    // NOT part of the immutable judgment — a stable accessor onto live market data (Entity-owned in spirit).
    livePrice?: () => number;
    expiration: string;
    template: string;
    economicReports: string[];
    zone: number | null;                // present in data; NOT displayed on the card (open ambiguity)
    // The FROZEN sentence. Still written by the mock skill, and the fallback the card renders when a
    // skill ships no `reason` parts. The real skill ships BOTH: `summary` for a non-live reader,
    // `reason` for the card.
    summary: string;
    // CR-20260904-001 (REQ-8): the reason line's PARTS, so the card can compose the sentence at
    // render time around the SAME live figure the header shows. A finished sentence cannot do that -
    // it freezes the credit at run time, which is exactly the demo defect (+$32.43 header against a
    // $32.38 reason). Absent => the card falls back to `summary`.
    reason?: RollSuggestionReason;
    previewKey: RollPreviewKey;         // drives advisory Preview
    adjustments: CashFlowAdjustment[];  // = the cell's adjustments, handed straight to Review
}

// Mutable host state. APG cards are ADVISORY ONLY — there is no Apply/Block (unlike the Hedge
// card); the sole mutable bit is whether this suggestion is currently being Previewed.
export interface RollSuggestionState {
    previewed: boolean;
}

export type RollSuggestionId = string;

// Entity (host identity + mutable state) wrapping the immutable Value content.
export interface RollSuggestion {
    id: RollSuggestionId;
    content: RollSuggestionContent;
    state: RollSuggestionState;
}

// ---- Run + Conversation ---------------------------------------------------------------

// 'pending'/'cancelled' are FE-only. 'empty' is a VALID "no suggestions" outcome, not a failure.
export type RunOutcome =
    | 'pending'
    | 'succeeded'
    | 'empty'
    | 'cancelled'
    | 'terminalFailed';

// ⚠ WHAT USED TO BE HERE: `RunCoverageNote` — the four-cause coverage note (calendar could not
// vouch / the platform did not finish pricing / the trader's own spread filters hid a day / a
// same-day expiration had no rate). It is GONE, with its whole rendering surface, on ruling R-C
// (CR-20260910-001, 2026-09-10): "no coverage note, no counts, no reported dropped, excluded,
// filtered or unpriced days or reasons."
//
// This is NOT the old failure coming back. The facts still exist and are still recorded — the
// backend writes every window date without a card to its own log with the reason (contract A1
// guarantee A-G5). What changed is the AUDIENCE: it is a support record, not a line on the panel.
// Two things survive on screen, and only two:
//   - `warnings` below — a statement about the RUN'S BASIS (the portfolio had no zone, so both
//     adjustments were priced), which is not a report about days the run dropped;
//   - the `empty` outcome's own sentence, because a blank panel reads as a failure.
// Do not reintroduce a count here without a new owner ruling.

export interface Run {
    id: string;
    tabKind: TabKind;
    sentInputs: RollPositionsInputs | null;   // snapshot of what was sent
    outcome: RunOutcome;
    startedAt: number;                        // epoch ms
    endedAt: number | null;
    failureDetail: string | null;
    // The backend's non-fatal notes for THIS run, shown verbatim (contract A1 §5 `Warnings[]`).
    // `ZoneUnavailable` is the one in service today: the portfolio had no current zone, so both
    // #2B and #3 were priced instead of one. Absent on runs that never reached the backend.
    warnings?: string[];
}

export interface Tab {
    kind: TabKind;
    inputs: RollPositionsInputs | null;
    currentRun: Run | null;                   // <=1 CURRENT run per tab; a new run supersedes
    suggestions: RollSuggestion[];            // from currentRun when 'succeeded'
}

// The Zone tab exists here only for uniformity — it stays inert forever (no run/inputs/cards).
export interface Conversation {
    id: string;
    portfolioId: string;
    tabs: { rollPositions: Tab; zone: Tab };
}
